import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { ExpertPicker } from '@/components/ExpertPicker';
import { SITE_MAP_KEY, writeScaffoldedPage, pushToSite, type ScaffoldWriteResult } from '@/data/client';

/* Write this page (docs/bulk-write-plan.md, Phase B): one scaffolded page,
   written from its scaffold, self-checked against the Page Health rubric and
   revised once if it scores low. On a staging site it is pushed on its own
   commit; on a live site the draft stops here for a person to read and push. */

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);
const fmt = (n: number | null) => (n == null ? 'not scored' : n.toFixed(1));

export function WritePageModal({ pageId, name, staging, onClose }: { pageId: string; name: string; staging: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [expertId, setExpertId] = useState('');
  const [mode, setMode] = useState<'polished' | 'strict'>('polished');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState<ScaffoldWriteResult | null>(null);
  const [draft, setDraft] = useState('');
  const [pushed, setPushed] = useState<string | null>(null);
  const [force, setForce] = useState(false);

  const write = async () => {
    setBusy(true); setErr('');
    try {
      const r = await writeScaffoldedPage(pageId, { expertId: expertId || undefined, mode });
      setResult(r.result);
      setDraft(r.result.content);
      if (r.result.pushed) setPushed(r.result.pushed.url);
      qc.invalidateQueries({ queryKey: SITE_MAP_KEY });
    } catch (e) { setErr(errMsg(e, 'The page could not be written.')); }
    finally { setBusy(false); }
  };
  const push = async () => {
    if (!result) return;
    setBusy(true); setErr('');
    try {
      const r = await pushToSite({ body: draft, kind: 'site_page', draft: false, path: result.path, force });
      setPushed(r.url);
      qc.invalidateQueries({ queryKey: SITE_MAP_KEY });
    } catch (e) { setErr(errMsg(e, 'Could not push.')); setForce(true); }
    finally { setBusy(false); }
  };

  const s = result?.score;
  return (
    <div className="admin-modal" role="dialog" aria-modal="true">
      <div className="admin-modal__scrim" onClick={busy ? undefined : onClose} />
      <div className="admin-modal__panel wb__rules">
        <div className="admin-modal__head">
          <div>
            <h3>Write {name}</h3>
            <p className="dash-muted">
              {staging
                ? 'Written from its scaffold and the Vault, checked against the Page Health rubric, then pushed to staging on its own commit.'
                : 'This site is live, so the draft stops here for you to read and push.'}
            </p>
          </div>
        </div>
        {err && <p className="gen-note smb__note is-err">{err}</p>}

        {!result && (
          <>
            <div className="smb__formgrid">
              <ExpertPicker value={expertId} onChange={setExpertId} label="Signed by" disabled={busy} approvedOnly />
              <Select label="Grounding" value={mode} disabled={busy} onChange={(e) => setMode(e.target.value as 'polished' | 'strict')} options={[{ value: 'polished', label: 'Polished — writes around a missing detail' }, { value: 'strict', label: 'Strict — marks a missing detail [NEEDS: …]' }]} />
            </div>
            <p className="dash-muted smb__meta">This takes a few minutes: one write, a score, and one revision if the score is under 5. FAQs are left to the FAQ editor.</p>
            <div className="wb__modalactions">
              <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>Cancel</Button>
              <Button variant="primary" size="sm" disabled={busy} onClick={write} iconLeft={<Icon name="edit" size={13} />}>{busy ? 'Writing…' : 'Write this page'}</Button>
            </div>
          </>
        )}

        {result && s && (
          <>
            <p className="smb__meta">
              <strong>Scored {fmt(s.final)}</strong> of 10 on effort and originality{s.revised ? `, after one revision (the first draft scored ${fmt(s.first)})` : ''}. Page Health scores it again once it is on the site.
            </p>
            {result.held && <p className="wb__warn"><Icon name="alert-circle" size={13} /> Not pushed: {result.held}</p>}
            {pushed && <p className="smb__meta"><Icon name="check" size={13} /> Pushed to <a href={pushed} target="_blank" rel="noreferrer">{result.path}</a>.</p>}
            {result.notes.length > 0 && <ul className="wb__notes">{result.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
            {!pushed && (
              <>
                <Textarea rows={16} value={draft} onChange={(e) => setDraft(e.target.value)} />
                <div className="wb__modalactions">
                  <Button variant="ghost" size="sm" disabled={busy} onClick={onClose}>Close without pushing</Button>
                  <Button variant="primary" size="sm" disabled={busy} onClick={push}>{busy ? 'Pushing…' : force ? 'Push anyway' : `Push to ${result.path}`}</Button>
                </div>
              </>
            )}
            {pushed && <div className="wb__modalactions"><Button variant="primary" size="sm" onClick={onClose}>Done</Button></div>}
          </>
        )}
      </div>
    </div>
  );
}
